import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import queryClient from 'src/services/queryClient';
import { CartDTO, CartStatus, CreateWaitingListCartDTO } from 'src/types';

import { createCart, createWaitingListCart, getCart, updateCart } from '../cart';

export function useCreateCart(eventId: number, promoter: string) {
  return useMutation({
    mutationFn: async (data: CartDTO) => createCart(eventId, data, promoter),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['cart'] });
    },
  });
}

export function useCreateWaitingListCart(eventId: number, promoter?: string) {
  return useMutation({
    mutationFn: async (data: CreateWaitingListCartDTO) =>
      createWaitingListCart(eventId, data, promoter),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['cart'] });
    },
  });
}

export function useUpdateCart(eventId: number, id: number, promoter: string) {
  return useMutation({
    mutationFn: async (data: CartDTO) => updateCart(eventId, id, data, promoter),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['cart', id] });
    },
  });
}

export function useGetOrderByCartId(cartId: number, alreadyPaid?: boolean) {
  const redirectionStatus = new URLSearchParams(window.location.search).get('redirect_status');
  const [shouldStopRefetching, setShouldStopRefetching] = useState(false);

  const result = useQuery({
    queryKey: ['cart', cartId, alreadyPaid, redirectionStatus, shouldStopRefetching],
    queryFn: async () => {
      const data = await getCart(cartId);

      // Stop refetching once we have a final status
      if (
        data?.status === CartStatus.PendingApproval ||
        data?.status === CartStatus.WaitListPending ||
        data?.status === CartStatus.Completed
      ) {
        setShouldStopRefetching(true);
      }

      return data;
    },
    refetchOnWindowFocus: !shouldStopRefetching,
    // Disable cache when redirect_status is 'succeeded' and we haven't reached a final state
    staleTime: redirectionStatus === 'succeeded' && !shouldStopRefetching ? 0 : 5 * 60 * 1000,
    // Force refetch when redirect_status is 'succeeded' until we reach a final state
    refetchInterval: redirectionStatus === 'succeeded' && !shouldStopRefetching ? 1000 : false,
    refetchIntervalInBackground: false,
    retry: redirectionStatus === 'succeeded' && !shouldStopRefetching ? 5 : 0,
    enabled: !!cartId,
  });

  // Keep loading state true until we have final data or stop refetching
  const enhancedIsLoading =
    result.isLoading ||
    (redirectionStatus === 'succeeded' &&
      !shouldStopRefetching &&
      result.data?.status !== CartStatus.PendingApproval &&
      result.data?.status !== CartStatus.Completed);

  return {
    ...result,
    isLoading: enhancedIsLoading,
    shouldStopRefetching,
  };
}
